import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDefaultSite } from '@/hooks/useDefaultSite';
import { useSiteIdentities } from '@/hooks/useIdentities';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import type { CommandLineForm } from './CommandLineView';

interface CollaborateurStepProps {
  form: UseFormReturn<CommandLineForm>;
}

export function CollaborateurStep({ form }: CollaborateurStepProps) {
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const { organization, isLoading: sitesLoading } = useDefaultSite();
  const sites = organization?.sites ?? [];

  const siteId = form.watch('siteId');
  const identityId = form.watch('identityId');

  const { data: identities = [], isLoading: identitiesLoading } =
    useSiteIdentities(siteId);

  const selectedIdentity = identities.find((i) => i.id === identityId);

  return (
    <div className="space-y-8">
      {/* Site section */}
      <section className="space-y-4">
        <h3 className="text-lg font-bold text-slate-900">
          Centre de facturation
        </h3>
        <FormField
          control={form.control}
          name="siteId"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={sitesLoading}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue
                      placeholder={
                        sitesLoading
                          ? 'Chargement...'
                          : 'Sélectionnez un centre de facturation'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </section>

      <div className="border-t border-slate-200" />

      {/* Identity section */}
      <section className="space-y-4">
        <h3 className="text-lg font-bold text-slate-900">
          Collaborateur
        </h3>

        {identityId && selectedIdentity ? (
          <div className="flex items-center gap-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50">
            <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-emerald-700">
                {selectedIdentity.firstName?.[0]}
                {selectedIdentity.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {selectedIdentity.firstName} {selectedIdentity.lastName}
              </p>
              {selectedIdentity.email && (
                <p className="text-xs text-slate-500 truncate">
                  {selectedIdentity.email}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-400 hover:text-red-500"
              onClick={() => form.setValue('identityId', '')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-slate-700">
                    Prénom
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Jean"
                      {...field}
                      disabled={!siteId}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-slate-700">
                    Nom
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Dupont"
                      {...field}
                      disabled={!siteId}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </section>

      {/* Select identity dialog */}
      <Dialog open={isSelectModalOpen} onOpenChange={setIsSelectModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              Sélectionner un collaborateur
            </DialogTitle>
          </DialogHeader>
          <FormField
            control={form.control}
            name="identityId"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <Command className="rounded-lg border border-slate-200">
                  <CommandInput
                    placeholder="Rechercher par nom ou email..."
                    className="h-10"
                  />
                  <CommandEmpty>
                    {identitiesLoading ? (
                      <div className="py-6 text-center">
                        <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
                        <p className="mt-2 text-sm text-slate-500">
                          Chargement...
                        </p>
                      </div>
                    ) : (
                      <p className="py-6 text-sm text-slate-500 text-center">
                        Aucun utilisateur trouvé
                      </p>
                    )}
                  </CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-auto">
                    {identities.map((identity) => (
                      <CommandItem
                        key={identity.id}
                        value={`${identity.firstName} ${identity.lastName} ${identity.email || ''}`}
                        onSelect={() => {
                          form.setValue('identityId', identity.id);
                          setIsSelectModalOpen(false);
                        }}
                        className="py-2.5"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <span className="text-xs font-medium text-slate-600">
                              {identity.firstName?.[0]}
                              {identity.lastName?.[0]}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-900">
                              {identity.firstName} {identity.lastName}
                            </span>
                            {identity.email && (
                              <span className="text-xs text-slate-500 block">
                                {identity.email}
                              </span>
                            )}
                          </div>
                          {field.value === identity.id && (
                            <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
                <FormMessage />
              </FormItem>
            )}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
